import { isDefined } from "@clipboard-health/util-ts";
import { useInviteToCaseChatOverride } from "@src/appV2/redesign/HomeHealthMyProfessionals/hooks/useInviteToCaseChatAction";
import { type ReactNode } from "react";

import { useInviteWorkerToShift } from "../components/InviteToShift/useInviteWorkerToShift";

interface UseChatInviteParams {
  workerId: string;
  workerName: string;
  workplaceId: string;
  /** When set, the surface is a placement channel — no invites of any kind. */
  placementId?: string;
  /**
   * Forwarded to the invite-to-shift dialog's qualification slot. Chat
   * surfaces with channel metadata pass `metadata.agentReq`.
   */
  channelQualification?: string;
}

interface UseChatInviteResult {
  /** Click handler for the chat invite button. Undefined hides the button. */
  onInvite: (() => void) | undefined;
  /** Label override (e.g. "Invite to case"). Undefined = built-in default. */
  inviteLabel: string | undefined;
  /** Invite dialog to render alongside the chat surface. */
  inviteDialogElement: ReactNode;
}

/**
 * Composes the invite affordance shared by chat surfaces (ChatPanel, the
 * full-page chat): Home Health workplace sessions get the self-gated
 * per-case invite, everyone else gets the built-in shift invite when
 * permitted, and placement channels get neither.
 */
export function useChatInvite(params: UseChatInviteParams): UseChatInviteResult {
  const { workerId, workerName, workplaceId, placementId, channelQualification } = params;

  // HH workplaces invite per-case (visit), not per-shift. The override hook
  // self-gates (undefined outside HH workplace sessions).
  const inviteToCaseOverride = useInviteToCaseChatOverride({ workerId, workerName, workplaceId });
  const inviteOverride = isDefined(placementId) ? undefined : inviteToCaseOverride;

  const { canInvite, openInviteDialog, inviteDialogElement } = useInviteWorkerToShift({
    workerId,
    workplaceId,
    surfaceDisabled: isDefined(placementId) || isDefined(inviteOverride),
    channelQualification,
  });

  return {
    onInvite: inviteOverride ? inviteOverride.onClick : canInvite ? openInviteDialog : undefined,
    inviteLabel: inviteOverride?.label,
    inviteDialogElement: inviteOverride ? inviteOverride.dialog : inviteDialogElement,
  };
}
